import { useCallback, useMemo } from "react"
import { Box, Typography, useTheme, Button } from "@mui/material"
import { useNavigate, Link } from "react-router-dom"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { getFeatureDetail } from "utils/feature"
import { Head } from "components/Head"
import { PageCard, PageCardContent } from "components/card"
import { MotionDiv } from "components/motions"
import { AppNavigation } from "containers/AppNavigation"
import { NavigationControl } from "components/kioskHorizontal/navigation"

import { EXTENSION_NAME } from "extensions/em-benchasiri-park/constant"
import { useExtension } from "hooks/extension/useExtension"
import { BenchasiriEventCard } from "extensions/em-benchasiri-park/components/Card"
import { useEmBenchasiri } from "extensions/em-benchasiri-park/hooks/useEmBenchasiri"

export const EMBenchasiriKioskHPage = ({ Layout = Box }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { events, findFeatureById } = useVenue()
  const { extensionConfig } = useExtension()
  const navigate = useNavigate()

  const benchasiriConfig = useMemo(
    () =>
      extensionConfig?.[EXTENSION_NAME]?.options || {
        location: { id: null },
      },
    [extensionConfig]
  )

  const locationFeature = useMemo(
    () => findFeatureById(benchasiriConfig?.location?.id),
    [benchasiriConfig?.location?.id, findFeatureById]
  )

  const handleClickViewLocation = useCallback(
    () => navigate(`/maps/place/${locationFeature.id}`),
    [locationFeature, navigate]
  )

  const {
    title,
    description,
    events: benchasiriEvents,
  } = useEmBenchasiri(locationFeature, events, benchasiriConfig)

  const pageStyleOverrides =
    theme.components[`EmBenchasiriPage`]?.styleOverrides || {}
  const bannerStyleOverrides =
    theme.components[`AppBanner`]?.styleOverrides || {}
  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}

  const { root } = bannerStyleOverrides
  const { wrapper, body, footer } = sideBarStyleOverrides
  const {
    detailContainer,
    pageContainer,
    titleText,
    titleWrapper,
    descriptionText,
    descriptionWrapper,
    contentContainer,
    ctaWrapper,
    eventSection,
    eventItemContainer,
  } = pageStyleOverrides
  return (
    <>
      <Head title={t("Benchasiri Park")} />
      <Layout
        aside={
          <PageCard>
            <Box sx={wrapper}>
              <Box sx={root}></Box>
              <Box sx={body}>
                <AppNavigation />
              </Box>
              <Box sx={footer}>
                <NavigationControl BackButtonProps={{ hidden: true }} />
              </Box>
            </Box>
          </PageCard>
        }
      >
        <PageCard>
          <MotionDiv
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.45, delay: 0.15 }}
            sx={pageContainer}
          >
            <PageCardContent sx={contentContainer}>
              <Box sx={detailContainer}>
                <Box sx={titleWrapper}>
                  <Typography variant="h1" sx={titleText}>
                    {title}
                  </Typography>
                </Box>
                <Box sx={descriptionWrapper}>
                  <Typography variant="body1" sx={descriptionText}>
                    {description}
                  </Typography>
                </Box>
                <Box sx={ctaWrapper}>
                  {locationFeature ? (
                    <Button
                      variant="contained"
                      onClick={handleClickViewLocation}
                    >
                      {t("View Location")}
                    </Button>
                  ) : null}
                </Box>
              </Box>
              <Box sx={eventSection}>
                {benchasiriEvents.map((event) => {
                  const { name, logo, startTime, endTime, id } =
                    getFeatureDetail(event)
                  return (
                    <Box sx={eventItemContainer} key={id}>
                      <BenchasiriEventCard
                        imageSrc={logo}
                        title={t(name)}
                        subtitle={
                          startTime && endTime
                            ? `${startTime} - ${endTime}`
                            : ""
                        }
                        component={Link}
                        to={`/events/${id}`}
                      />
                    </Box>
                  )
                })}
              </Box>
            </PageCardContent>
          </MotionDiv>
        </PageCard>
      </Layout>
    </>
  )
}
